import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, KeyRound, Save, CheckCircle2, AlertCircle, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-section security page:
 *   1) Change password (current + new + confirm)
 *   2) Security questions — pick 3 from a preset list (or write custom),
 *      provide answers, server bcrypt-hashes them.
 *
 * Without security questions set, the user cannot use the Forgot Password flow,
 * so we surface a clear warning at the top until they're configured.
 */
export const Security = () => {
  const { user } = useAuth();
  const [presets, setPresets] = useState([]);
  const [items, setItems] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' },
  ]);
  const [savingQ, setSavingQ] = useState(false);
  const [qError, setQError] = useState('');
  const [qInfo, setQInfo] = useState('');

  // Password change
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdInfo, setPwdInfo] = useState('');

  useEffect(() => {
    axios.get(`${API}/auth/security-questions/presets`)
      .then(({ data }) => setPresets(data.questions || []))
      .catch(() => setPresets([]));
  }, []);

  const updateItem = (i, k, v) => {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  };

  const saveQuestions = async (e) => {
    e.preventDefault();
    setQError(''); setQInfo('');
    // Client-side validation: questions distinct, answers non-empty (≥2 chars)
    const qs = items.map((it) => it.question.trim());
    if (qs.some((q) => !q)) { setQError('All 3 questions are required'); return; }
    if (new Set(qs).size !== 3) { setQError('Each question must be different'); return; }
    if (items.some((it) => (it.answer.trim().length < 2))) {
      setQError('Each answer must be at least 2 characters'); return;
    }
    setSavingQ(true);
    try {
      await axios.post(`${API}/auth/security-questions/set`, { items });
      setQInfo('Security questions saved. You can now use "Forgot password" if needed.');
      // Clear answers from memory after save
      setItems((arr) => arr.map((it) => ({ ...it, answer: '' })));
    } catch (err) {
      setQError(err?.response?.data?.detail || 'Failed to save');
    } finally { setSavingQ(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    setPwdError(''); setPwdInfo('');
    if (newPwd.length < 8) { setPwdError('New password must be at least 8 characters'); return; }
    if (newPwd !== newPwd2) { setPwdError('New passwords do not match'); return; }
    setSavingPwd(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setPwdInfo('Password changed.');
      setCurrentPwd(''); setNewPwd(''); setNewPwd2('');
    } catch (err) {
      setPwdError(err?.response?.data?.detail || 'Could not change password');
    } finally { setSavingPwd(false); }
  };

  return (
    <div className="space-y-6" data-testid="security-page">
      <header>
        <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
          <ShieldCheck className="h-6 w-6" /> Security Settings
        </h1>
        <p className="text-sm text-text-muted mt-1">
          Manage your password and security questions for password recovery. Logged in as <strong>{user?.email}</strong>.
        </p>
      </header>

      {!user?.has_security_questions && (
        <div className="bg-amber-50 border border-amber-300 rounded-md p-4 flex items-start gap-3" data-testid="security-no-questions-warning">
          <AlertCircle className="h-5 w-5 text-amber-800 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>Set your security questions now.</strong> Without them, you can't reset your password if forgotten —
            you'd have to ask an admin to manually reset it.
          </div>
        </div>
      )}

      {/* Password change */}
      <section className="bg-white border border-border rounded-md p-5 space-y-4" data-testid="security-change-password">
        <h2 className="text-base font-semibold text-primary flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Change Password
        </h2>
        <form onSubmit={changePwd} className="space-y-3 max-w-md">
          <Pwd label="Current password" value={currentPwd} onChange={setCurrentPwd} testid="cp-current" />
          <Pwd label="New password (min 8 chars)" value={newPwd} onChange={setNewPwd} testid="cp-new" />
          <Pwd label="Confirm new password" value={newPwd2} onChange={setNewPwd2} testid="cp-confirm" />
          {pwdError && <Alert kind="error" text={pwdError} testid="cp-error" />}
          {pwdInfo && <Alert kind="ok" text={pwdInfo} testid="cp-info" />}
          <Button type="submit" disabled={savingPwd} data-testid="cp-submit">
            <Save className="h-4 w-4 mr-1" /> {savingPwd ? 'Saving…' : 'Update Password'}
          </Button>
        </form>
      </section>

      {/* Security questions */}
      <section className="bg-white border border-border rounded-md p-5 space-y-4" data-testid="security-questions-section">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-primary flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Security Questions
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              Pick 3 different questions and provide answers. Answers are stored as bcrypt hashes (not reversible).
              Case and surrounding whitespace are ignored on verification.
            </p>
          </div>
        </div>

        <form onSubmit={saveQuestions} className="space-y-3 max-w-2xl">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-border pb-3 last:border-b-0 last:pb-0">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                  Question {i + 1}
                </label>
                <select
                  value={it.question}
                  onChange={(e) => updateItem(i, 'question', e.target.value)}
                  className="w-full border border-border rounded px-2 py-1.5 text-sm"
                  data-testid={`sq-question-${i}`}
                >
                  <option value="">— Choose a question —</option>
                  {presets.map((p) => <option key={p} value={p}>{p}</option>)}
                  <option value="__custom">+ Write my own…</option>
                </select>
                {it.question === '__custom' && (
                  <input
                    type="text"
                    autoFocus
                    placeholder="Type your own question"
                    onChange={(e) => updateItem(i, 'question', e.target.value)}
                    className="w-full border border-border rounded px-2 py-1.5 text-sm mt-1"
                    data-testid={`sq-custom-${i}`}
                  />
                )}
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                  Answer {i + 1}
                </label>
                <input
                  type="text"
                  value={it.answer}
                  onChange={(e) => updateItem(i, 'answer', e.target.value)}
                  placeholder="…"
                  className="w-full border border-border rounded px-2 py-1.5 text-sm"
                  data-testid={`sq-answer-${i}`}
                />
              </div>
            </div>
          ))}

          {qError && <Alert kind="error" text={qError} testid="sq-error" />}
          {qInfo && <Alert kind="ok" text={qInfo} testid="sq-info" />}

          <div className="flex gap-2">
            <Button type="submit" disabled={savingQ} data-testid="sq-submit">
              <Save className="h-4 w-4 mr-1" /> {savingQ ? 'Saving…' : 'Save Security Questions'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setItems([{question:'',answer:''},{question:'',answer:''},{question:'',answer:''}])}
              data-testid="sq-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
};

const Pwd = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">{label}</label>
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-border rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
      required
      data-testid={testid}
    />
  </div>
);

const Alert = ({ kind, text, testid }) => (
  <div
    className={`text-xs px-3 py-2 rounded border-l-4 flex items-start gap-2 ${
      kind === 'ok' ? 'bg-emerald-50 border-state-success text-emerald-900'
                    : 'bg-rose-50 border-state-error text-state-error'
    }`}
    data-testid={testid}
  >
    {kind === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                   : <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
    <span>{text}</span>
  </div>
);

export default Security;
